// Shared route-exclusion predicates for pjax navigation and prefetch.
// Single source of truth so the two systems can't drift.
//
// Besides the routes that always load in full, a page a Shoplift A/B test
// starts from does too, while the test runs (see shopliftDecides below).
//
// Also the history helpers theme code uses when it changes the address
// without a navigation (facets, infinite scroll, the variant picker): use
// them rather than history.pushState / replaceState directly, or pjax-max
// loses track of where back and forward lead.

const EXCLUDE_PATH_RE =
  /^\/(checkout|account|customer_authentication|cart|apps|services|community|tools|cart\/checkout)(\/|$)/i;
const FILE_EXT_RE =
  /\.(pdf|zip|rar|7z|jpg|jpeg|png|gif|webp|svg|mp4|mov|webm|csv|xlsx?|docx?)(\?|$)/i;

// URLs that cause server-side side effects when fetched — never prefetch.
// (A /discount/CODE link applies the code to the visitor's session.)
const SIDE_EFFECT_PATH_RE =
  /^\/(account\/logout|cart\/clear|cart\/change|discount)(\/|$)/i;

/** A URL's path without the locale prefix Shopify puts on it (`/fr/cart` → `/cart`). */
function routePath(url) {
  const root = window.Shopify?.routes?.root || "/";
  return root !== "/" && url.pathname.startsWith(root)
    ? url.pathname.slice(root.length - 1)
    : url.pathname;
}

export function shouldExcludeFromPjax(href) {
  let url;
  try {
    url = new URL(href, location.href);
  } catch {
    return true;
  }
  if (url.origin !== location.origin) return true;
  if (EXCLUDE_PATH_RE.test(routePath(url))) return true;
  if (FILE_EXT_RE.test(url.pathname)) return true;
  if (shopliftDecides(url)) return true;
  return false;
}

/**
 * Whether Shoplift has to see the page load. Shoplift, the store's A/B
 * testing app, runs once per page load (snippets/shoplift.liquid, at the top
 * of <head>): it enrols the visitor when they reach a page a test starts
 * from, and sends one in a variant on, to the page with `?view=<template>`
 * for a template test or to the variant's address for a URL test. Reached by
 * pjax, that page would show the original to everyone and count no one, so
 * while such a test runs, its pages load in full, in both arms: the test
 * then compares the templates, not a page load with a navigation. Its theme,
 * script, price and content tests apply to every page and carry on across
 * navigations. While a merchant previews a test, every page loads in full,
 * as Shoplift serves it.
 *
 * The tests are the ones Shoplift's snippet built into the page that loaded
 * (`window.shopliftInstance`); a test that starts later is seen at the next
 * page load.
 */
function shopliftDecides(url) {
  const shoplift = window.shopliftInstance;
  if (!shoplift) return false;
  if (shoplift.state?.temporary?.isMerchant) return true;

  const tests = Array.isArray(shoplift.testConfigs) ? shoplift.testConfigs : [];
  const type = shopliftPageType(routePath(url));

  return tests.some(
    (test) =>
      test.status === "active" &&
      test.hypotheses?.some(
        ({ isControl, type: tested, redirectPath }) =>
          isControl &&
          (tested === "urlRedirect" ? Boolean(redirectPath) && url.pathname.endsWith(redirectPath) : tested === type)
      )
  );
}

/**
 * The kind of page a path leads to, as Shoplift names the template a test
 * runs on (`template.name`, with list-collections as collectionList and page
 * as landing). A test on a template with a suffix runs on some pages of its
 * kind; which isn't known before the page is fetched, so all of them count.
 */
function shopliftPageType(path) {
  if (path === "/") return "index";
  if (/^\/(collections\/[^/]+\/)?products\/[^/]+\/?$/.test(path)) return "product";
  if (/^\/collections\/?$/.test(path)) return "collectionList";
  if (/^\/collections\/[^/]+/.test(path)) return "collection";
  if (/^\/pages\/[^/]+/.test(path)) return "landing";
  if (/^\/blogs\/[^/]+(\/tagged\/[^/]+)?\/?$/.test(path)) return "blog";
  if (/^\/blogs\/[^/]+\/[^/]+/.test(path)) return "article";
  if (/^\/search\/?$/.test(path)) return "search";
  if (/^\/cart\/?$/.test(path)) return "cart";
  return null;
}

export function shouldExcludeFromPrefetch(href) {
  if (shouldExcludeFromPjax(href)) return true;
  try {
    const url = new URL(href, location.href);
    if (SIDE_EFFECT_PATH_RE.test(routePath(url))) return true;
  } catch {
    return true;
  }
  return false;
}

/**
 * history.pushState, for an address change that isn't a navigation. pjax-max
 * numbers the history entries it can go back and forward to, and it numbers
 * one it didn't push (an in-page anchor's) when `hashchange` reports it, so
 * this reports a pushed entry the same way.
 */
export function pushHistoryState(state, url) {
  const oldURL = location.href;
  history.pushState(state, "", url);
  if (window.__pjax) {
    window.dispatchEvent(new HashChangeEvent("hashchange", { oldURL, newURL: location.href }));
  }
}

/**
 * history.replaceState, for an address change that isn't a navigation. Keeps
 * the entry's state, where pjax-max keeps its number (`history.state.pjaxMax`),
 * and tells the router where it is now.
 */
export function replaceHistoryState(url) {
  history.replaceState(history.state, "", url);
  const core = window.__pjax;
  if (core && !core.transitioning) {
    const { href, origin, pathname, hash } = new URL(location.href);
    core.location = { href, origin, pathname, anchor: hash || null };
  }
}
